import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import './index.css'
import AppErrorBoundary from './components/common/AppErrorBoundary'
import { useAuthStore } from './stores/authStore'
import { useAppStore } from './stores/appStore'

import { adoptAuthFromOtherTabs, startAuthShareResponder } from './utils/authTabShare'

// Restore persisted state
useAuthStore.getState().init()
useAppStore.getState().init()
// Başka sekmede açık oturum varsa bu sekme de onu kullanır (mail bağlantısı yeni sekmede açılır)
startAuthShareResponder()

// Deploy sonrası ESKİ chunk sorunu: açık sekme eski build ile gezinirken lazy sayfa chunk'ı
// artık sunucuda yok (yeni build hash'leri) → sayfa geçişinde beyaz ekran. Vite'ın resmi
// çözümü: preload hatasında sayfayı OTOMATİK yenile (yeni index.html + chunk'lar gelir).
// sessionStorage guard'ı ile route başına tek deneme (sonsuz reload döngüsü önlenir).
window.addEventListener('vite:preloadError', (event) => {
  const key = 'vertex_chunk_reload'
  const last = sessionStorage.getItem(key)
  const now = Date.now()
  if (!last || now - Number(last) > 10000) {  // 10 sn içinde ikinci kez patlarsa döngüye girme
    sessionStorage.setItem(key, String(now))
    event.preventDefault?.()
    window.location.reload()
  }
})

// Sürüm senkronu: eski sekme sorununu KÖKTEN çöz. Açık sekme eski build ile kalırsa
// (sunucuda eski chunk'lar dursa 404 olmaz → preloadError tetiklenmez → beyaz ekran/sayfa
// açılmaz). Çözüm: sunucudaki güncel index.html'in ana script hash'ini periyodik + sekme
// yeniden odaklanınca kontrol et; yüklü hash'ten FARKLIYSA yeni deploy var → sekmeyi yenile.
// index.html no-store ile gelir → fetch her zaman gerçek güncel sürümü verir. Döngü yok:
// yenileme sonrası DOM yeni hash'i taşır, tekrar tetiklenmez.
;(() => {
  const CUR = (() => {
    const el = document.querySelector('script[type=module][src*="/assets/index-"]') ||
               document.querySelector('script[src*="/assets/index-"]')
    const m = el && (el.getAttribute('src') || '').match(/index-[^./]+\.js/)
    return m ? m[0] : null
  })()
  if (!CUR) return
  let reloading = false
  // Backend ayağa kalkana kadar bekler (en çok ~60 sn). Kalkmazsa yine de yeniler:
  // eski build'de asılı kalmak da çalışan bir durum değil.
  const waitForBackend = async () => {
    for (let i = 0; i < 20; i++) {
      try {
        const r = await fetch('/api/v1/health?_=' + Date.now(), { cache: 'no-store' })
        if (r.ok) return
      } catch { /* backend henüz yok */ }
      await new Promise((res) => setTimeout(res, 3000))
    }
  }
  const check = async () => {
    if (reloading || document.visibilityState === 'hidden') return
    try {
      const html = await (await fetch('/?_v=' + Date.now(), { cache: 'no-store' })).text()
      const m = html.match(/assets\/(index-[^"./]+\.js)/)
      if (m && m[1] && m[1] !== CUR) {
        reloading = true
        // Deploy sırasında index.html YENİ, backend ise hâlâ yeniden başlıyor
        // (~16 sn: SIGTERM → uvicorn hazır). O anda yenilersek kullanıcı yeni
        // sayfayı açar ama TÜM API çağrıları patlar — ekranı ortasından kesip
        // hata yağmuruna tutmuş oluruz. Backend cevap verene kadar bekle.
        await waitForBackend()
        window.location.reload()
      }
    } catch { /* ağ hatası → sessiz geç */ }
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check()
  })
  setInterval(check, 60000)  // dakikada bir güncel sürüm kontrolü
  setTimeout(check, 5000)    // ilk açılıştan kısa süre sonra da bak
})()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      // gcTime 3dk: gözlemcisiz (sayfadan çıkılmış) sorgular 3dk sonra GC edilir.
      // Satış/CRM sayfalarındaki büyük fetch-all array'leri (5000 sipariş, 500 cari vb.)
      // oturum boyunca her filtre/gezinme kombinasyonunda 10dk birikmesin → RAM tırmanışı kesilir.
      gcTime: 1000 * 60 * 3,     // 3 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
})

const renderApp = () => ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </AppErrorBoundary>
  </React.StrictMode>,
)

// Oturumsuz açılan sekme önce açık sekmelere sorar (en çok ~0,35 sn), sonra çizilir
adoptAuthFromOtherTabs().then((adopted) => {
  if (adopted) {
    useAuthStore.getState().init()
    useAppStore.getState().init()
  }
}).finally(renderApp)
